// Leaving a circle and deleting the desyr ID (39-desyr-id-leaving.md): "Leave <Circle>?" from the
// switcher or My account, a circle the list no longer holds dropped quietly, My account's "Your
// circles" and "Delete my desyr ID". A circle goes from this device as signing out of one account;
// desyr ID and the other circles stay.
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useState, type ReactNode } from 'react';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Dialog, DialogButtons, ErrorLine } from '../blocks/Dialog';
import { Field } from '../blocks/Field';
import { useAction } from '../blocks/Wait';
import { config } from '../core/config';
import { ALL_OF_DESYR, clearStorageForSignOut, databasesLeft, deleteAllDatabases, ofDesyr } from '../core/device';
import { Said } from '../core/errors';
import { readSession } from '../core/session';
import { signedOutOf } from '../core/worker';
import { t } from '../i18n';
import { circleName, removeWaiting } from '../accounts/accounts';
import { CircleLogo } from '../accounts/AccountsView';
import { defaultAccount, setDefault, storePrefix, switchTo, waiting } from '../accounts/store';
import { stopPush } from '../push/push';
import { signOut } from '../session/client';
import { Card, Section } from '../settings/frame';
import { forgetEmail, lastEmail } from '../signin/company';
import { idCall, type Circle } from './api';
import { gone, leftCircle, listOf, loadCircles, noteAfter, useCircles, whileOpen } from './circles';
import { IdError, startSignIn } from './oidc';
import { roleWord } from './manage';
import { circlesCard } from './session';
import { keepAfter, keepReturn, readId, recentSignIn, takeAfter, type After } from './store';

/**
 * A circle goes from this device (39, Behaviour 1.3; 03, Signing out; 33, rule 2): a waiting one by
 * itself; the open one with its push (the last circle), databases, index and session, then the next
 * circle opens (the default, else the first waiting) or "Your circles". Its servers' failures are
 * ignored; `after` is the toast, shown now or after the reload.
 */
export function dropCircle(mxid: string, after: After, client?: MatrixClient): Promise<void> {
  // Once per circle in this page: a refused token reports its end once per request, and the circle
  // list's check may meet it; a second run would sign out the circle the first one just opened.
  if (!dropping.has(mxid)) dropping.set(mxid, drop(mxid, after, client));
  return dropping.get(mxid)!;
}
const dropping = new Map<string, Promise<void>>();

async function drop(mxid: string, after: After, client?: MatrixClient): Promise<void> {
  const open = readSession();
  const other = waiting().find((a) => a.user_id === mxid);
  if (other) return (await removeWaiting(other).catch(() => {}), keepAfter(after), noteAfter());
  if (open?.user_id !== mxid) return;
  client?.stopClient();
  signedOutOf(open.base_url);
  const others = waiting().filter((a) => !a.signed_out);
  const next = others.find((a) => a.user_id === defaultAccount()) ?? others[0];
  if (!next) await Promise.race([stopPush().catch(() => {}), new Promise((r) => setTimeout(r, 5000))]);
  await removeWaiting({ ...open, db: storePrefix() }).catch(() => {});
  keepAfter(after);
  if (!next) await deleteAllDatabases(ofDesyr).then(() => databasesLeft(ALL_OF_DESYR), () => {});
  clearStorageForSignOut(['desyr_id', 'desyr_id_leaving', ...(next ? ['desyr_accounts', 'desyr_default_account', 'desyr_mail', 'desyr_push_client'] : [])]);
  if (next) return switchTo(next.user_id, '/', circleName(next.user_id));
  circlesCard(true);
  location.replace('/');
}

/** A circle's session was refused (39, Behaviour 2.2): the list asked again; true when it no longer holds the circle, which then goes quietly. */
export async function removedFrom(mxid: string, client?: MatrixClient): Promise<boolean> {
  const id = config().desyrId;
  const answer = id && readId() ? await loadCircles(id) : undefined;
  if (!answer || answer.circles.some((c) => c.mxid === mxid)) return false;
  await dropCircle(mxid, { do: 'removed', name: circleName(mxid) }, client);
  return true;
}

/** desyr ID's sign-in first, in this window (39, Behaviour 1.4 and 3.1); back on this page the dialog opens again. `fresh`: asked again (prompt=login). */
async function signInAgain(after: After, fresh = false): Promise<never> {
  const here = location.pathname + location.search;
  keepAfter(after);
  keepReturn(here);
  await startSignIn(config().desyrId!, here, fresh ? { passkey: true } : undefined).catch((e) => {
    takeAfter('leave', 'delete');
    throw e instanceof IdError && e.reason === 'unavailable' ? new Said(t('id.unavailable')) : e;
  });
  return new Promise<never>(() => {}); // the window goes
}

/** The answer's code when it is one of `handled`; any other failure is thrown (desyr ID unreachable in its own words). */
const codeOf = (handled: string[]) => (e: unknown): string => {
  const code = (e as { code?: string } | null)?.code ?? '';
  if (handled.includes(code)) return code;
  throw code === 'desyr.id.unavailable' ? new Said(t('id.unavailable')) : e;
};

/** Both dialogs (39): the text, the typed confirmation (case and outer spaces ignored), or the owner's sentence with Close only. */
function Ask(props: {
  title: string;
  text: (owner?: string) => ReactNode;
  owner?: string;
  label: string;
  expected: string;
  action: string;
  busyText: string;
  failed: (reason: string) => string;
  /** Done when it resolves; the owner sentence when the service says the person owns the circle. */
  onConfirm: () => Promise<string | void>;
  onClose: () => void;
}) {
  const [said, setSaid] = useState<string>();
  const owner = said ?? props.owner;
  const [typed, setTyped] = useState('');
  const { run, busy, message } = useAction(async () => {
    const owns = await props.onConfirm();
    if (owns) setSaid(owns);
    else props.onClose();
  });
  const matches = typed.trim().toLowerCase() === props.expected.trim().toLowerCase();
  return (
    <Dialog title={props.title} alert onClose={busy ? () => {} : props.onClose}>
      <div className="dialog-text">{props.text(owner)}</div>
      {!owner && <Field label={props.label} placeholder={props.expected} value={typed} autoFocus onChange={(e) => setTyped(e.target.value)} />}
      <ErrorLine>{message && props.failed(message)}</ErrorLine>
      <DialogButtons>
        {owner ? (
          <Button variant="text" autoFocus onClick={props.onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button variant="text" disabled={busy} onClick={props.onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="critical-text" disabled={!matches || busy} onClick={() => void run()}>
              {busy ? props.busyText : props.action}
            </Button>
          </>
        )}
      </DialogButtons>
    </Dialog>
  );
}

/** "Leave <Circle>?" (39, Leaving a circle; Behaviour 1). */
function LeaveDialog({ client, circle, onClose }: { client: MatrixClient; circle: Circle; onClose: () => void }) {
  const owner = t('id.ownsCircle', { circle: circle.name });
  return (
    <Ask
      title={t('id.leaveTitle', { circle: circle.name })}
      text={(o) => (
        <>
          <p>{t('id.leaveText', { circle: circle.name })}</p>
          {o && <p>{o}</p>}
        </>
      )}
      owner={circle.role === 'owner' ? owner : undefined}
      label={t('id.typeCircle', { circle: circle.name })}
      expected={circle.name}
      action={t('id.leave')}
      busyText={t('id.leavingNow')}
      failed={(reason) => t('id.couldntLeave', { reason })}
      onConfirm={async () => {
        const code = await idCall(config().desyrId!, `/circles/${encodeURIComponent(circle.slug)}/leave`, { method: 'POST' }).then(
          () => undefined,
          codeOf(['desyr.circle.owner_must_hand_over', 'desyr.id.sign_in']),
        );
        if (code === 'desyr.circle.owner_must_hand_over') return owner;
        if (code) return signInAgain({ do: 'leave', slug: circle.slug });
        leftCircle(circle.server);
        await dropCircle(circle.mxid, { do: 'left', name: circle.name }, client);
      }}
      onClose={onClose}
    />
  );
}

let openLeave: ((slug: string) => void) | undefined;
/** "Leave <Circle>…" from the switcher's row menu or My account. */
export const leave = (slug: string) => openLeave?.(slug);

/**
 * desyr ID in the open app (36, Behaviour 2.1; 39, Behaviour 1–2): the toast kept over a reload, the
 * circles list asked in the background with every circle it no longer holds dropped quietly, and
 * "Leave <Circle>?" (also again after signing in to desyr ID).
 */
export function IdHost({ client }: { client: MatrixClient }) {
  const [slug, setSlug] = useState(() => takeAfter('leave')?.slug);
  const circle = useCircles().answer?.circles.find((c) => c.slug === slug);
  useEffect(() => {
    openLeave = setSlug;
    noteAfter();
    // The sessions call while desyr is open (36, Behaviour 6.6): new circles join the switcher, gone ones go quietly.
    const stop = whileOpen(config(), async (answer) => {
      for (const mxid of gone(answer.circles, [...waiting().map((a) => a.user_id), client.getUserId()!])) await dropCircle(mxid, { do: 'removed', name: circleName(mxid) }, client);
    });
    return () => void ((openLeave = undefined), stop());
  }, [client]);
  return circle ? <LeaveDialog key={slug} client={client} circle={circle} onClose={() => setSlug(undefined)} /> : null;
}

/** One row of "Your circles": the logo (32 px), the name, the role, the button. */
const Row = ({ logo, name, line, children }: { logo: ReactNode; name: string; line?: string; children?: ReactNode }) => (
  <li className="settings-card">
    <div className="settings-card-row">
      {logo}
      <div className="settings-card-text">
        <div className="settings-card-title">{name}</div>
        {line && <div className="settings-card-line">{line}</div>}
      </div>
      {children}
    </div>
  </li>
);

/** My account's "Your circles" (39): every circle of the desyr ID with its role and Leave; while the list can't load, the circles on this device. */
export function CirclesSection({ client }: { client: MatrixClient }) {
  const { answer, unreachable, logos } = useCircles();
  const id = config().desyrId!;
  useEffect(() => void loadCircles(id), []);
  return (
    <section className="settings-section">
      <h3>{t('id.circles')}</h3>
      <ul className="settings-cards id-my-circles" aria-label={t('id.circles')}>
        {unreachable
          ? [client.getUserId()!, ...waiting().map((a) => a.user_id)].map((u) => <Row key={u} logo={<CircleLogo userId={u} size={32} />} name={circleName(u)} />)
          : answer?.circles.map((c) => (
              <Row key={c.server} logo={<Avatar id={c.server} name={c.name} src={logos[c.slug]} size={32} round={false} letters={2} />} name={c.name} line={roleWord(c.role)}>
                <Button small onClick={() => leave(c.slug)}>
                  {t('id.leaveItem', { circle: c.name })}
                </Button>
              </Row>
            ))}
      </ul>
      {unreachable && (
        <p className="settings-note">
          {t('id.cantLoad')}{' '}
          <Button small variant="text" onClick={() => void loadCircles(id)}>
            {t('id.retry')}
          </Button>
        </p>
      )}
    </section>
  );
}

/**
 * Done deleting (39, Behaviour 3.4): this device cleared as signing out of desyr ID (36, Behaviour 4)
 * without its end-session address, the servers' failures ignored, the last email forgotten when it
 * is this desyr ID's; the signed-out page then shows the note.
 */
async function clearDeleted(client: MatrixClient, email: string): Promise<void> {
  for (const a of waiting()) await removeWaiting(a).catch(() => {});
  setDefault(null);
  if (lastEmail().toLowerCase() === email.toLowerCase()) forgetEmail();
  keepAfter({ do: 'deleted' });
  await signOut(client, '/login').catch(() => (clearStorageForSignOut(), location.replace('/login')));
}

/** "Delete your desyr ID?" (39, Deleting; Behaviour 3). */
function DeleteDialog({ client, onClose }: { client: MatrixClient; onClose: () => void }) {
  const id = config().desyrId!;
  const email = readId()?.email ?? '';
  const circles = useCircles().answer?.circles ?? [];
  const ownerOf = (list: Circle[]) => {
    const owned = list.filter((c) => c.role === 'owner').map((c) => c.name);
    return owned.length ? t('id.ownsCircle', { circle: listOf(owned) }) : undefined;
  };
  const lead = circles.length > 1 ? t('id.deleteSeveral', { n: circles.length, names: listOf(circles.map((c) => c.name)) }) : circles[0] && t('id.deleteOne', { circle: circles[0].name });
  return (
    <Ask
      title={t('id.deleteTitle')}
      text={(o) => ((o ?? lead) ? `${o ?? lead} ${t('id.deleteRest')}` : t('id.deleteNone'))}
      owner={ownerOf(circles)}
      label={t('id.typeEmail')}
      expected={email}
      action={t('id.deleteForGood')}
      busyText={t('id.deleting')}
      failed={(reason) => t('id.couldntDelete', { reason })}
      onConfirm={async () => {
        // The "passkey" option is desyr ID's fresh sign-in: prompt=login and max_age=0.
        if (!recentSignIn(readId())) return signInAgain({ do: 'delete' }, true);
        const code = await idCall(id, '/me', { method: 'DELETE' }).then(() => undefined, codeOf(['desyr.id.owner_of', 'desyr.id.sign_in_again', 'desyr.id.sign_in']));
        if (code === 'desyr.id.owner_of') return ownerOf((await loadCircles(id))?.circles ?? circles) ?? Promise.reject(Object.assign(new Error(code), { status: 409, code }));
        if (code) return signInAgain({ do: 'delete' }, true);
        await clearDeleted(client, email);
      }}
      onClose={onClose}
    />
  );
}

/** The last card of My account (39): "Delete my desyr ID…", open again after signing in for it. */
export function DeleteIdSection({ client }: { client: MatrixClient }) {
  const [open, setOpen] = useState(false);
  // Opened again after desyr ID's sign-in (39, Behaviour 3): a frame later, so it lies over the Settings window.
  useEffect(() => void (takeAfter('delete') && requestAnimationFrame(() => setOpen(true))), []);
  return (
    <Section>
      <Card
        title={t('id.deleteCard')}
        text={t('id.deleteLine')}
        control={
          <Button small variant="critical" onClick={() => setOpen(true)}>
            {t('id.deleteButton')}
          </Button>
        }
      />
      {open && <DeleteDialog client={client} onClose={() => setOpen(false)} />}
    </Section>
  );
}
